/**
 * The room's QR code, and the Share action that sits in the corner next to it
 * (SPEC §5.1).
 *
 * One implementation, and now one place that uses it: the top of the lobby.
 * There was a second, full-screen `/invite` screen carrying the same QR and
 * the same code; the two copies drifted once already, with the invite screen
 * excellent and the lobby showing a small "Show QR" button instead of a QR
 * code — exactly the "hidden behind a button" failure §5.1 exists to prevent.
 * With only the lobby left there is nothing to drift.
 */
import { useState } from 'react';
import QRCode from 'react-qr-code';
import { roomUrl } from '../../lib/roomCode.js';
import { shareRoom } from '../../lib/share.js';

/**
 * The code sits in a cream card, on cream modules, with the quiet-zone the card
 * padding provides — scanners need a light margin and the surrounding page is
 * sand, not paper.
 *
 * Size *and* quiet-zone come from the caller, and neither is defaulted here:
 * two padding utilities in one class attribute are resolved by stylesheet
 * order, not by the order they are written, so a base `p-4` plus a caller's
 * `p-5` is a coin toss dressed up as an override.
 */
export function RoomQrCode({ roomCode, className }: { roomCode: string; className: string }) {
  return (
    <div className={`rounded-card mx-auto w-full border border-line bg-cream ${className}`}>
      <QRCode
        value={roomUrl(roomCode)}
        size={256}
        level="M"
        bgColor="#fff7e7"
        fgColor="#263127"
        style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
        title={`Join room ${roomCode}`}
      />
    </div>
  );
}

/** SPEC §5.1 — Share lives in the corner, for the person who is not at the table. */
export function ShareButton({ roomCode }: { roomCode: string }) {
  const [copied, setCopied] = useState(false);

  const share = async () => {
    const outcome = await shareRoom({ roomCode, url: roomUrl(roomCode) });
    if (outcome === 'copied') {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void share()}
      // `normal-case` because this sits in the status row, which is uppercased
      // for eyebrows — and no button in this app is uppercase.
      className="rounded-btn flex min-h-11 items-center gap-1.5 border border-line bg-cream px-3 text-[0.8125rem] font-semibold text-ink normal-case"
    >
      <span aria-hidden="true">↗</span>
      {copied ? 'Copied' : 'Share'}
    </button>
  );
}
